import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import logger from '../../utils/logger';

import {
  ArrowLeftIcon,
  ClockIcon,
  UserIcon,
  ChartBarIcon,
  DocumentTextIcon,
  QuestionMarkCircleIcon,
  ChevronDownIcon,
  ChevronRightIcon,
  ShieldCheckIcon,
  CameraIcon,
  ComputerDesktopIcon,
  ClipboardDocumentCheckIcon,
} from '@heroicons/react/24/outline';

const AssessmentView = () => {
  const { id: assessmentId } = useParams();
  const navigate = useNavigate();

  const [assessment, setAssessment] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expandedTests, setExpandedTests] = useState(new Set());
  const [proctoringConfig, setProctoringConfig] = useState(null);
  const [proctoringLoading, setProctoringLoading] = useState(false);
  const [proctoringExpanded, setProctoringExpanded] = useState(false);

  // Helper function to format question type labels
  const getQuestionTypeLabel = (type) => {
    if (!type) return 'Unknown';
    const typeLower = type.toLowerCase();
    if (typeLower.includes('multiple') && typeLower.includes('choice')) {
      if (typeLower.includes('multiple') && !typeLower.includes('single')) {
        return 'Multiple Choice (Multiple Answers)';
      }
      return 'Multiple Choice (Single Answer)';
    }
    if (typeLower.includes('true') || typeLower.includes('false')) {
      return 'True/False';
    }
    if (typeLower.includes('short')) {
      return 'Short Answer';
    }
    if (typeLower.includes('essay')) {
      return 'Essay';
    }
    return type; // Return original if no match
  };

  const fetchAssessment = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axios.get(`/api/assessments/${assessmentId}`);
      logger.log('API Response:', response.data);
      logger.log('Custom Questions:', response.data.assessment?.content?.customQuestions);
      if (response.data.assessment?.content?.customQuestions) {
        response.data.assessment.content.customQuestions.forEach((q, i) => {
          logger.log(`Custom Question ${i + 1}:`, {
            type: q.type,
            options: q.options,
            optionsLength: q.options?.length,
            correctAnswer: q.correctAnswer,
            full: q,
          });
        });
      }
      setAssessment(response.data.assessment);
    } catch (error) {
      console.error('Error fetching assessment:', error);
      navigate('/dashboard/assessments');
    } finally {
      setLoading(false);
    }
  }, [assessmentId, navigate]);

  const fetchProctoringConfig = useCallback(async () => {
    try {
      setProctoringLoading(true);
      const response = await axios.get(`/api/proctoring/config/${assessmentId}`);
      setProctoringConfig(response.data.config);
    } catch (error) {
      console.error('Error fetching proctoring config:', error);
      // Proctoring config not found is not a critical error
      setProctoringConfig(null);
    } finally {
      setProctoringLoading(false);
    }
  }, [assessmentId]);

  useEffect(() => {
    if (assessmentId) {
      fetchAssessment();
      fetchProctoringConfig();
    }
  }, [assessmentId, fetchAssessment, fetchProctoringConfig]);

  // Debug: Log assessment data when it changes
  useEffect(() => {
    if (assessment) {
      logger.log('📋 Assessment data received:', assessment);
      logger.log('📋 Assessment content:', assessment.content);
      if (assessment.content && assessment.content.customQuestions) {
        logger.log('📋 Custom Questions array:', assessment.content.customQuestions);
        logger.log('📋 Custom Questions length:', assessment.content.customQuestions.length);
        assessment.content.customQuestions.forEach((q, i) => {
          logger.log(`📋 Custom Question ${i + 1}:`, {
            _id: q._id,
            title: q.title,
            content: q.content,
            type: q.type,
            options: q.options,
            optionsLength: q.options?.length,
            correctAnswer: q.correctAnswer,
            points: q.points,
            full: q,
          });
        });
      }
      if (assessment.content && assessment.content.questions) {
        logger.log('📋 Questions from library:', assessment.content.questions);
      }
    }
  }, [assessment]);

  const toggleTestExpansion = (testId) => {
    setExpandedTests((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(testId)) {
        newSet.delete(testId);
      } else {
        newSet.add(testId);
      }
      return newSet;
    });
  };

  const toggleProctoringExpansion = () => {
    setProctoringExpanded((prev) => !prev);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 py-8">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="animate-pulse">
            <div className="h-8 bg-gray-200 rounded w-1/4 mb-6"></div>
            <div className="h-64 bg-gray-200 rounded"></div>
          </div>
        </div>
      </div>
    );
  }

  if (!assessment) {
    return (
      <div className="min-h-screen bg-gray-50 py-8">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h1 className="text-2xl font-bold text-gray-900 mb-4">Assessment not found</h1>
            <button
              onClick={() => navigate('/dashboard/assessments')}
              className="text-indigo-600 hover:text-indigo-500"
            >
              Back to Assessments
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Safety check for content
  if (!assessment.content) {
    console.error('❌ Assessment has no content property:', assessment);
    return (
      <div className="min-h-screen bg-gray-50 py-8">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h1 className="text-2xl font-bold text-gray-900 mb-4">Assessment content not found</h1>
            <button
              onClick={() => navigate('/dashboard/assessments')}
              className="text-indigo-600 hover:text-indigo-500"
            >
              Back to Assessments
            </button>
          </div>
        </div>
      </div>
    );
  }

  const totalContent =
    (assessment.content.tests?.length || 0) +
    (assessment.content.questions?.length || 0) +
    (assessment.content.customQuestions?.length || 0);

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <button
            onClick={() => navigate('/dashboard/assessments')}
            className="flex items-center text-sm text-gray-600 hover:text-gray-900 mb-4"
          >
            <ArrowLeftIcon className="h-4 w-4 mr-2" />
            Back to Assessments
          </button>

          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">{assessment.title}</h1>
              <p className="text-gray-600 mt-2">{assessment.description}</p>
            </div>

            <div className="flex items-center space-x-2">
              <span
                className={`px-3 py-1 rounded-full text-sm font-medium ${
                  assessment.status === 'published'
                    ? 'bg-green-100 text-green-800'
                    : assessment.status === 'draft'
                      ? 'bg-yellow-100 text-yellow-800'
                      : assessment.status === 'ended'
                        ? 'bg-red-100 text-red-800'
                        : 'bg-gray-100 text-gray-800'
                }`}
              >
                {assessment.status.charAt(0).toUpperCase() + assessment.status.slice(1)}
              </span>
            </div>
          </div>
        </div>

        {/* Assessment Overview */}
        <div className="bg-white rounded-lg shadow p-6 mb-8">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">Assessment Overview</h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Basic Information */}
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-3">Basic Information</h3>
              <div className="space-y-3">
                <div className="flex items-center text-sm text-gray-600">
                  <DocumentTextIcon className="h-4 w-4 mr-2" />
                  <span className="font-medium">Title:</span>
                  <span className="ml-2">{assessment.title}</span>
                </div>
                <div className="flex items-center text-sm text-gray-600">
                  <UserIcon className="h-4 w-4 mr-2" />
                  <span className="font-medium">Role:</span>
                  <span className="ml-2">{assessment.role}</span>
                </div>
                <div className="flex items-center text-sm text-gray-600">
                  <ClockIcon className="h-4 w-4 mr-2" />
                  <span className="font-medium">Duration:</span>
                  <span className="ml-2">{assessment.duration} minutes</span>
                </div>
                <div className="flex items-center text-sm text-gray-600">
                  <ChartBarIcon className="h-4 w-4 mr-2" />
                  <span className="font-medium">Passing Score:</span>
                  <span className="ml-2">{assessment.passingScore}%</span>
                </div>
                {assessment.status === 'ended' && assessment.endedAt && (
                  <div className="flex items-center text-sm text-gray-600">
                    <ClockIcon className="h-4 w-4 mr-2" />
                    <span className="font-medium">Ended:</span>
                    <span className="ml-2">
                      {new Date(assessment.endedAt).toLocaleDateString()}
                    </span>
                  </div>
                )}
              </div>
            </div>

            {/* Content Summary */}
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-3">Content Summary</h3>
              <div className="space-y-3">
                <div className="flex items-center text-sm text-gray-600">
                  <DocumentTextIcon className="h-4 w-4 mr-2" />
                  <span className="font-medium">Tests:</span>
                  <span className="ml-2">{assessment.content.tests.length}</span>
                </div>
                <div className="flex items-center text-sm text-gray-600">
                  <QuestionMarkCircleIcon className="h-4 w-4 mr-2" />
                  <span className="font-medium">Total Questions:</span>
                  <span className="ml-2">{assessment.totalQuestions || 0}</span>
                </div>
                <div className="flex items-center text-sm text-gray-600">
                  <DocumentTextIcon className="h-4 w-4 mr-2" />
                  <span className="font-medium">Custom Questions:</span>
                  <span className="ml-2">{assessment.content.customQuestions?.length || 0}</span>
                </div>
                <div className="flex items-center text-sm text-gray-600">
                  <ChartBarIcon className="h-4 w-4 mr-2" />
                  <span className="font-medium">Total Points:</span>
                  <span className="ml-2">{assessment.totalPoints || 0}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Description */}
          {assessment.description && (
            <div className="mt-6">
              <h3 className="text-lg font-medium text-gray-900 mb-3">Description</h3>
              <p className="text-gray-600">{assessment.description}</p>
            </div>
          )}

          {/* Instructions */}
          {assessment.instructions && (
            <div className="mt-6">
              <h3 className="text-lg font-medium text-gray-900 mb-3">
                Instructions for Candidates
              </h3>
              <p className="text-gray-600">{assessment.instructions}</p>
            </div>
          )}
        </div>

        {/* Proctoring Configuration */}
        <div className="bg-white rounded-lg shadow p-6 mb-8">
          <div
            className="flex items-center justify-between mb-4 cursor-pointer hover:bg-gray-50 p-2 rounded-lg transition-colors"
            onClick={toggleProctoringExpansion}
          >
            <div className="flex items-center">
              <ShieldCheckIcon className="h-6 w-6 text-indigo-600 mr-2" />
              <h2 className="text-xl font-semibold text-gray-900">Proctoring Configuration</h2>
            </div>
            {proctoringExpanded ? (
              <ChevronDownIcon className="h-5 w-5 text-gray-500" />
            ) : (
              <ChevronRightIcon className="h-5 w-5 text-gray-500" />
            )}
          </div>

          {/* Proctoring Summary when collapsed */}
          {!proctoringExpanded && (
            <div className="text-sm text-gray-600 mb-4">
              {proctoringConfig ? (
                Object.keys(proctoringConfig).filter((key) => proctoringConfig[key]?.enabled)
                  .length > 0 ? (
                  <div className="flex flex-wrap gap-2">
                    <span className="text-gray-500">Enabled features:</span>
                    {proctoringConfig.fullScreen?.enabled && (
                      <span className="px-2 py-1 bg-blue-100 text-blue-800 rounded text-xs">
                        Full Screen
                      </span>
                    )}
                    {proctoringConfig.tabProctoring?.enabled && (
                      <span className="px-2 py-1 bg-green-100 text-green-800 rounded text-xs">
                        Tab Monitoring
                      </span>
                    )}
                    {proctoringConfig.aiDetection?.enabled && (
                      <span className="px-2 py-1 bg-orange-100 text-orange-800 rounded text-xs">
                        AI Detection
                      </span>
                    )}
                    {proctoringConfig.copyPastePrevention?.enabled && (
                      <span className="px-2 py-1 bg-red-100 text-red-800 rounded text-xs">
                        Copy/Paste Block
                      </span>
                    )}
                    {proctoringConfig.mouseMonitoring?.enabled && (
                      <span className="px-2 py-1 bg-indigo-100 text-indigo-800 rounded text-xs">
                        Mouse Tracking
                      </span>
                    )}
                  </div>
                ) : (
                  <span className="text-gray-500">No proctoring features enabled</span>
                )
              ) : (
                <span className="text-gray-500">No proctoring configuration found</span>
              )}
            </div>
          )}

          {proctoringExpanded && (
            <>
              {proctoringLoading ? (
                <div className="animate-pulse">
                  <div className="h-4 bg-gray-200 rounded w-1/4 mb-4"></div>
                  <div className="h-32 bg-gray-200 rounded"></div>
                </div>
              ) : proctoringConfig ? (
                <div className="space-y-6">
                  {/* Full Screen Monitoring */}
                  {proctoringConfig.fullScreen?.enabled && (
                    <div className="border border-gray-200 rounded-lg p-4">
                      <div className="flex items-center mb-3">
                        <ComputerDesktopIcon className="h-5 w-5 text-blue-600 mr-2" />
                        <h3 className="text-lg font-medium text-gray-900">
                          Full Screen Monitoring
                        </h3>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                        <div className="flex items-center justify-between">
                          <span className="text-gray-600">Force Full Screen:</span>
                          <span
                            className={`px-2 py-1 rounded text-xs font-medium ${
                              proctoringConfig.fullScreen.forceFullScreen
                                ? 'bg-red-100 text-red-800'
                                : 'bg-gray-100 text-gray-800'
                            }`}
                          >
                            {proctoringConfig.fullScreen.forceFullScreen ? 'Yes' : 'No'}
                          </span>
                        </div>
                        <div className="flex items-center justify-between">
                          <span className="text-gray-600">Tolerance Limit:</span>
                          <span className="px-2 py-1 bg-blue-100 text-blue-800 rounded text-xs font-medium">
                            {proctoringConfig.fullScreen.toleranceLimit} exits allowed
                          </span>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Tab Switching Monitoring */}
                  {proctoringConfig.tabProctoring?.enabled && (
                    <div className="border border-gray-200 rounded-lg p-4">
                      <div className="flex items-center mb-3">
                        <ClipboardDocumentCheckIcon className="h-5 w-5 text-green-600 mr-2" />
                        <h3 className="text-lg font-medium text-gray-900">
                          Tab Switching Monitoring
                        </h3>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                        <div className="flex items-center justify-between">
                          <span className="text-gray-600">Block Tab Switching:</span>
                          <span
                            className={`px-2 py-1 rounded text-xs font-medium ${
                              proctoringConfig.tabProctoring.blockTabSwitch
                                ? 'bg-red-100 text-red-800'
                                : 'bg-gray-100 text-gray-800'
                            }`}
                          >
                            {proctoringConfig.tabProctoring.blockTabSwitch ? 'Yes' : 'No'}
                          </span>
                        </div>
                        <div className="flex items-center justify-between">
                          <span className="text-gray-600">Tolerance Limit:</span>
                          <span className="px-2 py-1 bg-green-100 text-green-800 rounded text-xs font-medium">
                            {proctoringConfig.tabProctoring.toleranceLimit} switches allowed
                          </span>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* AI Detection */}
                  {proctoringConfig.aiDetection?.enabled && (
                    <div className="border border-gray-200 rounded-lg p-4">
                      <div className="flex items-center mb-3">
                        <ShieldCheckIcon className="h-5 w-5 text-orange-600 mr-2" />
                        <h3 className="text-lg font-medium text-gray-900">
                          AI Assistance Detection
                        </h3>
                      </div>
                      <div className="space-y-2 text-sm">
                        {proctoringConfig.aiDetection.detectCopyPaste && (
                          <div className="flex items-center">
                            <span className="w-2 h-2 bg-orange-500 rounded-full mr-2"></span>
                            <span className="text-gray-600">Copy/Paste Activity Detection</span>
                          </div>
                        )}
                        {proctoringConfig.aiDetection.detectMultipleScreens && (
                          <div className="flex items-center">
                            <span className="w-2 h-2 bg-orange-500 rounded-full mr-2"></span>
                            <span className="text-gray-600">Multiple Screens Detection</span>
                          </div>
                        )}
                        {proctoringConfig.aiDetection.detectMouseLeave && (
                          <div className="flex items-center">
                            <span className="w-2 h-2 bg-orange-500 rounded-full mr-2"></span>
                            <span className="text-gray-600">Mouse Leave Detection</span>
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Copy/Paste Prevention */}
                  {proctoringConfig.copyPastePrevention?.enabled && (
                    <div className="border border-gray-200 rounded-lg p-4">
                      <div className="flex items-center mb-3">
                        <ClipboardDocumentCheckIcon className="h-5 w-5 text-red-600 mr-2" />
                        <h3 className="text-lg font-medium text-gray-900">Copy/Paste Prevention</h3>
                      </div>
                      <div className="text-sm">
                        <div className="flex items-center justify-between">
                          <span className="text-gray-600">Tolerance Limit:</span>
                          <span className="px-2 py-1 bg-red-100 text-red-800 rounded text-xs font-medium">
                            {proctoringConfig.copyPastePrevention.toleranceLimit} attempts allowed
                          </span>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Mouse Monitoring */}
                  {proctoringConfig.mouseMonitoring?.enabled && (
                    <div className="border border-gray-200 rounded-lg p-4">
                      <div className="flex items-center mb-3">
                        <ComputerDesktopIcon className="h-5 w-5 text-indigo-600 mr-2" />
                        <h3 className="text-lg font-medium text-gray-900">
                          Mouse Activity Monitoring
                        </h3>
                      </div>
                      <div className="text-sm">
                        <div className="flex items-center justify-between">
                          <span className="text-gray-600">Tolerance Limit:</span>
                          <span className="px-2 py-1 bg-indigo-100 text-indigo-800 rounded text-xs font-medium">
                            {proctoringConfig.mouseMonitoring.toleranceLimit} leaves allowed
                          </span>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Data Retention */}
                  {proctoringConfig.dataRetention && (
                    <div className="border border-gray-200 rounded-lg p-4">
                      <div className="flex items-center mb-3">
                        <ClipboardDocumentCheckIcon className="h-5 w-5 text-gray-600 mr-2" />
                        <h3 className="text-lg font-medium text-gray-900">Data Retention</h3>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                        <div className="flex items-center justify-between">
                          <span className="text-gray-600">Retention Period:</span>
                          <span className="px-2 py-1 bg-gray-100 text-gray-800 rounded text-xs font-medium">
                            {proctoringConfig.dataRetention.days} days
                          </span>
                        </div>
                        <div className="flex items-center justify-between">
                          <span className="text-gray-600">Auto-Delete:</span>
                          <span
                            className={`px-2 py-1 rounded text-xs font-medium ${
                              proctoringConfig.dataRetention.autoDelete
                                ? 'bg-green-100 text-green-800'
                                : 'bg-gray-100 text-gray-800'
                            }`}
                          >
                            {proctoringConfig.dataRetention.autoDelete ? 'Yes' : 'No'}
                          </span>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <div className="text-center py-8 text-gray-500">
                  <ShieldCheckIcon className="h-16 w-16 mx-auto text-gray-400 mb-4" />
                  <h3 className="text-lg font-medium text-gray-900 mb-2">
                    No Proctoring Configuration
                  </h3>
                  <p className="text-gray-500 mb-4">
                    This assessment doesn't have any proctoring settings configured.
                  </p>
                  <button
                    onClick={() => navigate(`/dashboard/assessments/${assessmentId}/edit`)}
                    className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700"
                  >
                    Configure Proctoring
                  </button>
                </div>
              )}
            </>
          )}
        </div>

        {/* Content Preview */}
        <div className="bg-white rounded-lg shadow p-6 mb-8">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-semibold text-gray-900">Content Preview</h2>
          </div>

          {/* Tests */}
          {assessment.content.tests && assessment.content.tests.length > 0 && (
            <div className="mb-6">
              <h3 className="text-lg font-medium text-gray-900 mb-3">Tests from Library</h3>
              <div className="space-y-3">
                {assessment.content.tests.map((testItem, index) => (
                  <div
                    key={testItem.test}
                    className="border border-gray-200 rounded-lg overflow-hidden"
                  >
                    {/* Test Header */}
                    <div
                      className="flex items-center justify-between p-4 bg-gray-50 cursor-pointer hover:bg-gray-100 transition-colors"
                      onClick={() => toggleTestExpansion(testItem.test._id)}
                    >
                      <div className="flex items-center">
                        <span className="text-sm font-medium text-gray-500 mr-3">#{index + 1}</span>
                        <div>
                          <div className="font-medium text-gray-900">{testItem.test.title}</div>
                          <div className="text-sm text-gray-600">{testItem.test.description}</div>
                          <div className="text-xs text-gray-500 mt-1">
                            {testItem.test.numberOfQuestions} questions •{' '}
                            {testItem.test.estimatedTime} min
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center space-x-2">
                        <span
                          className={`px-2 py-1 rounded-full text-xs ${
                            testItem.test.difficulty === 'Easy'
                              ? 'bg-green-100 text-green-800'
                              : testItem.test.difficulty === 'Medium'
                                ? 'bg-yellow-100 text-yellow-800'
                                : 'bg-red-100 text-red-800'
                          }`}
                        >
                          {testItem.test.difficulty}
                        </span>
                        {expandedTests.has(testItem.test._id) ? (
                          <ChevronDownIcon className="h-4 w-4 text-gray-500" />
                        ) : (
                          <ChevronRightIcon className="h-4 w-4 text-gray-500" />
                        )}
                      </div>
                    </div>

                    {/* Test Questions (Expandable) */}
                    {expandedTests.has(testItem.test._id) && testItem.test.questions && (
                      <div className="border-t border-gray-200 bg-white p-4">
                        <h4 className="text-sm font-medium text-gray-700 mb-3">
                          Questions in this test:
                        </h4>
                        <div className="space-y-3">
                          {testItem.test.questions.map((questionItem, qIndex) => {
                            const question = questionItem.question; // Access the populated question
                            if (!question) return null;

                            return (
                              <div
                                key={question._id || qIndex}
                                className="pl-4 border-l-2 border-gray-200"
                              >
                                <div className="text-sm font-medium text-gray-900 mb-2">
                                  Q{qIndex + 1}: {question.title}
                                </div>

                                {/* Only show content if it's different from title */}
                                {question.content && question.content !== question.title && (
                                  <div className="text-xs text-gray-600 mb-2">
                                    {question.content}
                                  </div>
                                )}

                                {/* Show options for multiple choice and True/False questions */}
                                {(question.type === 'Multiple Choice (Single)' ||
                                  question.type === 'Multiple Choice (Multiple)' ||
                                  question.type === 'True/False' ||
                                  question.type === 'true-false' ||
                                  question.type === 'multiple-choice' ||
                                  question.type === 'Multiple Choice (Single Answer)' ||
                                  question.type === 'Multiple Choice (Multiple Answers)') &&
                                  question.options &&
                                  question.options.length > 0 && (
                                    <div className="mb-2">
                                      <div className="text-xs font-medium text-gray-700 mb-1">
                                        Options:
                                      </div>
                                      <div className="space-y-1">
                                        {question.options.map((option, optIndex) => (
                                          <div
                                            key={optIndex}
                                            className="flex items-center space-x-2"
                                          >
                                            <span
                                              className={`text-xs px-2 py-1 rounded ${
                                                option.isCorrect
                                                  ? 'bg-green-100 text-green-800 font-medium'
                                                  : 'bg-gray-100 text-gray-600'
                                              }`}
                                            >
                                              {String.fromCharCode(65 + optIndex)}. {option.text}
                                            </span>
                                            {option.isCorrect && (
                                              <span className="text-xs text-green-600 font-medium">
                                                ✓ Correct
                                              </span>
                                            )}
                                          </div>
                                        ))}
                                      </div>
                                    </div>
                                  )}

                                {/* Show correct answer for short-answer, essay, and other non-MCQ questions */}
                                {question.type !== 'multiple-choice' &&
                                  question.type !== 'Multiple Choice (Single)' &&
                                  question.type !== 'Multiple Choice (Multiple)' &&
                                  question.type !== 'True/False' &&
                                  question.type !== 'true-false' &&
                                  question.type !== 'Multiple Choice (Single Answer)' &&
                                  question.type !== 'Multiple Choice (Multiple Answers)' &&
                                  question.correctAnswer && (
                                    <div className="mb-2">
                                      <div className="text-xs font-medium text-gray-700">
                                        Correct Answer:
                                      </div>
                                      <div className="text-xs text-gray-600 bg-green-50 px-2 py-1 rounded">
                                        {question.correctAnswer}
                                      </div>
                                    </div>
                                  )}

                                <div className="text-xs text-gray-500 mt-2 pt-2 border-t border-gray-100">
                                  <span className="font-medium text-gray-700">Type:</span>{' '}
                                  {getQuestionTypeLabel(question.type)} •{' '}
                                  <span className="font-medium text-gray-700">Points:</span>{' '}
                                  {questionItem.points || question.points || 1} point
                                  {(questionItem.points || question.points || 1) !== 1 ? 's' : ''}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Questions */}
          {assessment.content.questions && assessment.content.questions.length > 0 && (
            <div className="mb-6">
              <h3 className="text-lg font-medium text-gray-900 mb-3">Questions from Library</h3>
              <div className="space-y-3">
                {assessment.content.questions.map((questionItem, index) => {
                  const question = questionItem.question;
                  if (!question) return null;

                  return (
                    <div
                      key={question._id || index}
                      className="border border-gray-200 rounded-lg overflow-hidden"
                    >
                      {/* Question Header */}
                      <div className="p-4 bg-gray-50">
                        <div className="flex items-center">
                          <span className="text-sm font-medium text-gray-500 mr-3">
                            #{index + 1}
                          </span>
                          <div className="flex-1">
                            <div className="font-medium text-gray-900">{question.title}</div>
                            {question.content && question.content !== question.title && (
                              <div className="text-sm text-gray-600">{question.content}</div>
                            )}
                          </div>
                        </div>
                      </div>

                      {/* Question Details */}
                      <div className="p-4 bg-white border-t border-gray-200">
                        {/* Show options for multiple choice and True/False questions */}
                        {(question.type === 'Multiple Choice (Single)' ||
                          question.type === 'Multiple Choice (Multiple)' ||
                          question.type === 'True/False' ||
                          question.type === 'true-false' ||
                          question.type === 'multiple-choice' ||
                          question.type === 'Multiple Choice (Single Answer)' ||
                          question.type === 'Multiple Choice (Multiple Answers)') &&
                          question.options &&
                          question.options.length > 0 && (
                            <div className="mb-3">
                              <div className="text-sm font-medium text-gray-700 mb-2">Options:</div>
                              <div className="space-y-2">
                                {question.options.map((option, optIndex) => (
                                  <div key={optIndex} className="flex items-center space-x-2">
                                    <span
                                      className={`text-sm px-3 py-2 rounded border ${
                                        option.isCorrect
                                          ? 'bg-green-100 text-green-800 border-green-300 font-medium'
                                          : 'bg-gray-50 text-gray-700 border-gray-200'
                                      }`}
                                    >
                                      {String.fromCharCode(65 + optIndex)}. {option.text}
                                    </span>
                                    {option.isCorrect && (
                                      <span className="text-sm text-green-600 font-medium">
                                        ✓ Correct
                                      </span>
                                    )}
                                  </div>
                                ))}
                              </div>
                              {/* Show summary of correct answers for multiple choice */}
                              {(question.type === 'multiple-choice' ||
                                question.type === 'Multiple Choice (Multiple)' ||
                                question.type === 'Multiple Choice (Multiple Answers)') && (
                                <div className="mt-3">
                                  <div className="text-sm font-medium text-gray-700 mb-1">
                                    Correct Answer(s):
                                  </div>
                                  <div className="text-sm text-gray-600 bg-green-50 px-3 py-2 rounded border border-green-200 font-medium">
                                    {(() => {
                                      const correctOptions = question.options
                                        .map((opt, idx) =>
                                          opt.isCorrect ? String.fromCharCode(65 + idx) : null
                                        )
                                        .filter(Boolean);
                                      return correctOptions.length > 0
                                        ? correctOptions.join(', ')
                                        : 'None';
                                    })()}
                                  </div>
                                </div>
                              )}
                              {/* Show correct answer for single-answer multiple choice */}
                              {(question.type === 'Multiple Choice (Single)' ||
                                question.type === 'Multiple Choice (Single Answer)' ||
                                (question.type === 'multiple-choice' &&
                                  question.options.filter((opt) => opt.isCorrect).length ===
                                    1)) && (
                                <div className="mt-3">
                                  <div className="text-sm font-medium text-gray-700 mb-1">
                                    Correct Answer:
                                  </div>
                                  <div className="text-sm text-gray-600 bg-green-50 px-3 py-2 rounded border border-green-200 font-medium">
                                    {(() => {
                                      const correctOption = question.options.find(
                                        (opt, idx) => opt.isCorrect
                                      );
                                      if (correctOption) {
                                        const index = question.options.indexOf(correctOption);
                                        return String.fromCharCode(65 + index);
                                      }
                                      return 'None';
                                    })()}
                                  </div>
                                </div>
                              )}
                            </div>
                          )}

                        {/* Show correct answer for short-answer, essay, and other non-MCQ questions */}
                        {question.type !== 'multiple-choice' &&
                          question.type !== 'Multiple Choice (Single)' &&
                          question.type !== 'Multiple Choice (Multiple)' &&
                          question.type !== 'True/False' &&
                          question.type !== 'true-false' &&
                          question.type !== 'Multiple Choice (Single Answer)' &&
                          question.type !== 'Multiple Choice (Multiple Answers)' &&
                          question.correctAnswer && (
                            <div className="mb-3">
                              <div className="text-sm font-medium text-gray-700 mb-1">
                                Correct Answer:
                              </div>
                              <div className="text-sm text-gray-600 bg-green-50 px-3 py-2 rounded border border-green-200 font-medium">
                                {question.correctAnswer}
                              </div>
                            </div>
                          )}
                        {/* Show correct answer for True/False questions */}
                        {(question.type === 'True/False' || question.type === 'true-false') &&
                          question.correctAnswer && (
                            <div className="mb-3">
                              <div className="text-sm font-medium text-gray-700 mb-1">
                                Correct Answer:
                              </div>
                              <div className="text-sm text-gray-600 bg-green-50 px-3 py-2 rounded border border-green-200 font-medium">
                                {question.correctAnswer === 'a' || question.correctAnswer === 'True'
                                  ? 'True'
                                  : 'False'}
                              </div>
                            </div>
                          )}

                        <div className="flex items-center justify-between mt-3 pt-3 border-t border-gray-100">
                          <div className="text-xs text-gray-500">
                            <span className="font-medium text-gray-700">Type:</span>{' '}
                            {getQuestionTypeLabel(question.type)} •{' '}
                            <span className="font-medium text-gray-700">Points:</span>{' '}
                            {questionItem.points || question.points || 1} point
                            {(questionItem.points || question.points || 1) !== 1 ? 's' : ''}
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Custom Questions */}
          {(() => {
            logger.log('🔍 Rendering Custom Questions section:', {
              hasAssessment: !!assessment,
              hasContent: !!assessment?.content,
              hasCustomQuestions: !!assessment?.content?.customQuestions,
              customQuestionsLength: assessment?.content?.customQuestions?.length || 0,
              customQuestions: assessment?.content?.customQuestions,
            });
            return assessment?.content?.customQuestions?.length > 0;
          })() && (
            <div className="mb-6">
              <h3 className="text-lg font-medium text-gray-900 mb-3">Custom Questions</h3>
              <div className="space-y-3">
                {assessment.content.customQuestions.map((question, index) => {
                  // Debug: Log question data
                  logger.log(`🔍 Rendering Question ${index + 1}:`, {
                    type: question.type,
                    hasOptions: !!question.options,
                    optionsLength: question.options?.length || 0,
                    options: question.options,
                    correctAnswer: question.correctAnswer,
                    points: question.points,
                    fullQuestion: question,
                  });

                  return (
                    <div
                      key={question._id}
                      className="border border-gray-200 rounded-lg overflow-hidden"
                    >
                      {/* Question Header */}
                      <div className="p-4 bg-gray-50">
                        <div className="flex items-center">
                          <span className="text-sm font-medium text-gray-500 mr-3">
                            #{index + 1}
                          </span>
                          <div className="flex-1">
                            <div className="font-medium text-gray-900">{question.title}</div>
                            {question.content && question.content !== question.title && (
                              <div className="text-sm text-gray-600">{question.content}</div>
                            )}
                          </div>
                        </div>
                      </div>

                      {/* Question Details */}
                      <div className="p-4 bg-white border-t border-gray-200">
                        {/* Show options for multiple choice and True/False questions */}
                        {(() => {
                          const isMCQOrTF =
                            question.type === 'Multiple Choice (Single)' ||
                            question.type === 'Multiple Choice (Multiple)' ||
                            question.type === 'True/False' ||
                            question.type === 'true-false' ||
                            question.type === 'multiple-choice' ||
                            question.type === 'Multiple Choice (Single Answer)' ||
                            question.type === 'Multiple Choice (Multiple Answers)';

                          const hasOptions =
                            question.options &&
                            Array.isArray(question.options) &&
                            question.options.length > 0;

                          logger.log(`Question ${index + 1} display check:`, {
                            type: question.type,
                            isMCQOrTF,
                            hasOptions,
                            options: question.options,
                            optionsType: typeof question.options,
                          });

                          return isMCQOrTF && hasOptions;
                        })() && (
                          <div className="mb-3">
                            <div className="text-sm font-medium text-gray-700 mb-2">Options:</div>
                            <div className="space-y-2">
                              {question.options.map((option, optIndex) => (
                                <div key={optIndex} className="flex items-center space-x-2">
                                  <span
                                    className={`text-sm px-3 py-2 rounded border ${
                                      option.isCorrect
                                        ? 'bg-green-100 text-green-800 border-green-300 font-medium'
                                        : 'bg-gray-50 text-gray-700 border-gray-200'
                                    }`}
                                  >
                                    {String.fromCharCode(65 + optIndex)}. {option.text}
                                  </span>
                                  {option.isCorrect && (
                                    <span className="text-xs text-green-600 font-medium">
                                      ✓ Correct
                                    </span>
                                  )}
                                </div>
                              ))}
                            </div>
                            {/* Show summary of correct answers for multiple choice */}
                            {(() => {
                              if (!question.options || !Array.isArray(question.options))
                                return null;
                              const correctCount = question.options.filter(
                                (opt) => opt && opt.isCorrect
                              ).length;
                              if (
                                correctCount > 1 ||
                                question.type === 'multiple-choice' ||
                                question.type === 'Multiple Choice (Multiple)' ||
                                question.type === 'Multiple Choice (Multiple Answers)'
                              ) {
                                return (
                                  <div className="mt-3">
                                    <div className="text-sm font-medium text-gray-700 mb-1">
                                      Correct Answer(s):
                                    </div>
                                    <div className="text-sm text-gray-600 bg-green-50 px-3 py-2 rounded border border-green-200 font-medium">
                                      {(() => {
                                        const correctOptions = question.options
                                          .map((opt, idx) =>
                                            opt && opt.isCorrect
                                              ? String.fromCharCode(65 + idx)
                                              : null
                                          )
                                          .filter(Boolean);
                                        return correctOptions.length > 0
                                          ? correctOptions.join(', ')
                                          : 'None';
                                      })()}
                                    </div>
                                  </div>
                                );
                              } else if (correctCount === 1) {
                                return (
                                  <div className="mt-3">
                                    <div className="text-sm font-medium text-gray-700 mb-1">
                                      Correct Answer:
                                    </div>
                                    <div className="text-sm text-gray-600 bg-green-50 px-3 py-2 rounded border border-green-200 font-medium">
                                      {(() => {
                                        const correctOption = question.options.find(
                                          (opt) => opt && opt.isCorrect
                                        );
                                        if (correctOption) {
                                          const index = question.options.indexOf(correctOption);
                                          return String.fromCharCode(65 + index);
                                        }
                                        return 'None';
                                      })()}
                                    </div>
                                  </div>
                                );
                              }
                              return null;
                            })()}
                          </div>
                        )}

                        {/* Fallback: If options don't exist but we have correctAnswer, show it */}
                        {(!question.options ||
                          !Array.isArray(question.options) ||
                          question.options.length === 0) &&
                          (question.type === 'multiple-choice' ||
                            question.type === 'Multiple Choice (Single)' ||
                            question.type === 'Multiple Choice (Multiple)' ||
                            question.type === 'Multiple Choice (Single Answer)' ||
                            question.type === 'Multiple Choice (Multiple Answers)') &&
                          question.correctAnswer && (
                            <div className="mb-3">
                              <div className="text-sm font-medium text-gray-700 mb-1">
                                Correct Answer:
                              </div>
                              <div className="text-sm text-gray-600 bg-green-50 px-3 py-2 rounded border border-green-200 font-medium">
                                {question.correctAnswer}
                              </div>
                            </div>
                          )}

                        {/* Show correct answer for short-answer, essay, and other non-MCQ questions */}
                        {question.type !== 'multiple-choice' &&
                          question.type !== 'Multiple Choice (Single)' &&
                          question.type !== 'Multiple Choice (Multiple)' &&
                          question.type !== 'True/False' &&
                          question.type !== 'true-false' &&
                          question.type !== 'Multiple Choice (Single Answer)' &&
                          question.type !== 'Multiple Choice (Multiple Answers)' &&
                          question.correctAnswer && (
                            <div className="mb-3">
                              <div className="text-sm font-medium text-gray-700 mb-1">
                                Correct Answer:
                              </div>
                              <div className="text-sm text-gray-600 bg-green-50 px-3 py-2 rounded border border-green-200 font-medium">
                                {question.correctAnswer}
                              </div>
                            </div>
                          )}
                        {/* Show correct answer for True/False questions */}
                        {(question.type === 'True/False' || question.type === 'true-false') &&
                          question.correctAnswer && (
                            <div className="mb-3">
                              <div className="text-sm font-medium text-gray-700 mb-1">
                                Correct Answer:
                              </div>
                              <div className="text-sm text-gray-600 bg-green-50 px-3 py-2 rounded border border-green-200 font-medium">
                                {question.correctAnswer === 'a' || question.correctAnswer === 'True'
                                  ? 'True'
                                  : 'False'}
                              </div>
                            </div>
                          )}

                        <div className="flex items-center justify-between mt-3 pt-3 border-t border-gray-100">
                          <div className="text-xs text-gray-500">
                            <span className="font-medium text-gray-700">Type:</span>{' '}
                            {getQuestionTypeLabel(question.type)} •{' '}
                            <span className="font-medium text-gray-700">Points:</span>{' '}
                            {question.points || 1} point{(question.points || 1) !== 1 ? 's' : ''}
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* No Content Warning */}
          {totalContent === 0 && (
            <div className="text-center py-8 text-gray-500">
              <QuestionMarkCircleIcon className="h-16 w-16 mx-auto text-gray-400 mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">No content added</h3>
              <p className="text-gray-600">This assessment has no content.</p>
            </div>
          )}
        </div>

        {/* Action Buttons */}
        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          <button
            onClick={() => navigate('/dashboard/assessments')}
            className="px-6 py-3 border border-gray-300 rounded-md text-base font-medium text-gray-700 bg-white hover:bg-gray-50 transition-colors"
          >
            Back to Assessments
          </button>

          <button
            onClick={() => navigate(`/dashboard/assessments/${assessmentId}/edit`)}
            className="px-6 py-3 border border-blue-300 rounded-md text-base font-medium text-blue-700 bg-white hover:bg-blue-50 transition-colors"
          >
            Edit Assessment
          </button>

          <button
            onClick={() => navigate(`/dashboard/assessments/${assessmentId}/invite`)}
            className="px-6 py-3 border border-transparent rounded-md text-base font-medium text-white bg-indigo-600 hover:bg-indigo-700 transition-colors"
          >
            Invite Candidates
          </button>
        </div>
      </div>
    </div>
  );
};

export default AssessmentView;
